import './App.css'
import { MainRouter } from './container/MainRouter'
import axios from "axios"
import { ThemeProvider } from './theme'
import { CookiesProvider } from 'react-cookie'
import { DataDogService, setDatadogAction, setDatadogError, setGlobalContext } from './services/datadog'
import { useEffect, useState, useMemo } from 'react'
import Bowser from 'bowser'

function App() {
  axios.defaults.baseURL = process.env.REACT_APP_SIGN_API_URL

  if (process.env.REACT_APP_ENV !== 'local') {
    DataDogService.init()
  }

  useEffect(() => {
    const dsScript = document.createElement('script')
    dsScript.src = 'https://ds.viewpkg.com/device-scan-1-1-0.js'
    dsScript.defer = true
    dsScript.onload = () => {
      setDatadogAction('device loaded');
    }
    dsScript.onerror = (error) => {
      setDatadogError('Device error occurred', { error });
    }
    document.body.appendChild(dsScript)

    return () => {
      document.body.removeChild(dsScript)
    }
  }, [])

  useEffect(() => {
    const ocrScript = document.createElement('script')
    ocrScript.src = 'https://ocr.caas.qitech.app/3-1-0/ocr.js'
    ocrScript.defer = true
    ocrScript.onload = () => {
      setDatadogAction('ocr loaded');
    }
    ocrScript.onerror = (error) => {
      setDatadogError('OCR error occurred', { error });
    }
    document.body.appendChild(ocrScript)

    return () => {
      document.body.removeChild(ocrScript)
    }
  }, [])

  useEffect(() => {
    const faceReconScript = document.createElement('script')
    faceReconScript.src = 'https://facerecon.zaig.com.br/face-recognition-3-3-0.js'
    faceReconScript.defer = true
    faceReconScript.onload = () => {
      setDatadogAction('face recognition loaded');
    }
    faceReconScript.onerror = (error) => {
      setDatadogError('Face recognition error occurred', { error });
    }
    document.body.appendChild(faceReconScript)

    return () => {
      document.body.removeChild(faceReconScript)
    }
  }, [])

  const [osInformation, setOsInformation] = useState(null);
  const [bowserOsInformation, setBowserOsInformation] = useState(null);
  const [browserInformation, setBrowserInformation] = useState(null);
  const [userIp, setUserIp] = useState(null);
  const [fetchedIpOnce, setFetchedIpOnce] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchIp = async () => {
      try {
        const response = await axios.get('https://api.ipify.org?format=json', { timeout: 5000 });
        if (!cancelled && response && response.data && response.data.ip) {
          setUserIp(String(response.data.ip));
          setDatadogAction('Fetched user public IP', { ip: String(response.data.ip) });
        }
      } catch (error) {
        setDatadogAction('Failed to fetch user public IP', { error: error });
      } finally {
        setFetchedIpOnce(true);
      }
    };
    fetchIp();
    const intervalId = setInterval(fetchIp, 60000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, []);

  const getOsInformation = async () => {
    try {
      const parser = Bowser.getParser(window.navigator.userAgent);
      const bowserOsInfo = parser.getOS();
      const bowserBrowserInfo = parser.getBrowser();
      
      const uaData = navigator.userAgentData;
      if (uaData && typeof uaData.getHighEntropyValues === 'function') {
        try {
          const ua = await uaData.getHighEntropyValues([
            'platform',
            'platformVersion',
            'model',
            'mobile'
          ]);
          const platform = String(ua.platform || '').toLowerCase();
          if (ua.platformVersion) {
            const major = parseInt(String(ua.platformVersion).split('.')[0], 10);
            if (!Number.isNaN(major)) {
              const osInfo = { name: platform, version: String(major) };
              setDatadogAction('OS Extracted information (UA-CH + Bowser)', { ...ua, os: osInfo, bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
              setGlobalContext({ ...ua, os: osInfo, bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
              return { os: osInfo, browser: bowserBrowserInfo, bowserOs: bowserOsInfo };
            }
          }
        } catch (e) {
          setDatadogError('UA-CH extraction failed', { error: e });
        }
      }

      setDatadogAction('OS Extracted information (Bowser only)', { bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
      setGlobalContext({ bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
      return { os: null, browser: bowserBrowserInfo, bowserOs: bowserOsInfo };
    } catch (error) {
      console.setDatadogError('[Startup] Failed to parse OS info with Bowser:', error);
      return { os: null, browser: null, bowserOs: null };
    }
  }

  useEffect(() => {
    let cancelled = false;
    const updateOs = async () => {
      const { os, browser, bowserOs } = await getOsInformation();
      if (cancelled) return;
      setOsInformation((prev) => {
        if (!prev) return os;
        const prevRaw = JSON.stringify(prev);
        const nextRaw = JSON.stringify(os);
        return prevRaw === nextRaw ? prev : os;
      });
      setBowserOsInformation((prev) => {
        if (!prev) return bowserOs;
        const prevRaw = JSON.stringify(prev);
        const nextRaw = JSON.stringify(bowserOs);
        return prevRaw === nextRaw ? prev : bowserOs;
      });
      setBrowserInformation((prev) => {
        if (!prev) return browser;
        const prevRaw = JSON.stringify(prev);
        const nextRaw = JSON.stringify(browser);
        return prevRaw === nextRaw ? prev : browser;
      });
    };
    updateOs();
    const intervalId = setInterval(updateOs, 10000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, []);

  const isInvalidOS = useMemo(() => {

    const os = osInformation ?? bowserOsInformation;
    if (!os || !os.name) return false;

    const isLinux = String(os.name).toLowerCase() === 'linux';
    setDatadogAction(`userIp (Ipify) - ${userIp}`, { ipifyIp: userIp });
    if (isLinux && fetchedIpOnce) {
      if (!userIp) return true;
      if (!['186.224.219.198', '201.6.229.166'].includes(String(userIp).trim())) {
        setDatadogAction('Linux outside of viplisted zones - Blocked', { ipifyIp: userIp });
        return true;
      }
    }


    if (!os.version) return false;

    const major = parseInt(String(os.version).split('.')[0], 10);
    if (Number.isNaN(major)) return false;
    
    const isAndroid = String(os.name).toLowerCase().includes('android');
    if (isAndroid && major < 9) {
      return true;
    }

    const isIOS = String(os.name).toLowerCase().includes('ios');
    if (isIOS && major < 15) {
      return true;
    }

    return false;
  
  }, [osInformation, bowserOsInformation, userIp, fetchedIpOnce]);

  const isInvalidBrowser = useMemo(() => {
    if (!browserInformation || !browserInformation.name || !browserInformation.version) return false;

    if (![
      'chrome',
      'safari',
      'samsung internet for android',
      'miui',
      'google search',
      'microsoft edge',
      'firefox',
      'opera'
    ].includes(browserInformation.name.toLowerCase())) return true;

    const isChromeMobile = String(browserInformation.name).toLowerCase().includes('chrome');
    if (!isChromeMobile) return false;
    const major = parseInt(String(browserInformation.version).split('.')[0], 10);
    if (Number.isNaN(major)) return false;
    return major < 121;
  }, [browserInformation]);

  return (
    <CookiesProvider>
      <ThemeProvider>
        <MainRouter isInvalidOS={isInvalidOS} isInvalidBrowser={isInvalidBrowser}/>
      </ThemeProvider>
    </CookiesProvider>
  )
}

export default App
